import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Droplets, ScanLine, Waves, Layers, FlaskConical, ClipboardCheck, PackageOpen, ShieldCheck, Sparkles, Diamond } from "lucide-react";
import { ASSETS } from "@/lib/api";

const CATEGORIES = [
  { name: "Pokémon Cards", to: "/pokemon-card-cleaning", copy: "Holo, vintage and modern Pokémon cards, evaluated by finish." },
  { name: "Sports Cards", to: "/sports-card-cleaning", copy: "Chrome, refractors and inserts across every major sport." },
  { name: "Baseball Cards", to: "/baseball-card-cleaning", copy: "Vintage and modern baseball, from commons to grails." },
  { name: "Football Cards", to: "/sports-card-cleaning", copy: "Rookies, autos and patches handled by card type." },
  { name: "Basketball Cards", to: "/sports-card-cleaning", copy: "Prizm, Select and vintage hardwood classics." },
  { name: "Hockey Cards", to: "/sports-card-cleaning", copy: "Young Guns and vintage ice, finish-aware care." },
  { name: "Soccer Cards", to: "/sports-card-cleaning", copy: "UCL, World Cup and league releases worldwide." },
  { name: "TCG Cards", to: "/card-services", copy: "Magic, Lorcana and other trading card games." },
  { name: "Vintage Trading Cards", to: "/baseball-card-cleaning", copy: "Fragile stock and older finishes, handled conservatively." },
  { name: "Modern Trading Cards", to: "/card-services", copy: "Glossy modern coatings and premium parallels." },
  { name: "Other Approved Collectibles", to: "/submit", copy: "Send photos first — we evaluate candidacy individually." },
];

const SERVICES = [
  { name: "Surface Cleaning", icon: Droplets, to: "/services/trading-card-surface-cleaning", copy: "Safely remove dirt, oils, residue and surface contaminants." },
  { name: "Scratch Reduction", icon: ScanLine, to: "/services/trading-card-scratch-reduction", copy: "Reduce the visibility of eligible light surface scratches." },
  { name: "Dent & Indention Reduction", icon: Waves, to: "/services/trading-card-dent-reduction", copy: "Minimize small dents, pressure marks and indentions." },
  { name: "Crease Minimization", icon: Layers, to: "/services/trading-card-crease-repair", copy: "Reduce the appearance of eligible creases and folds." },
  { name: "Card Rehabilitation", icon: FlaskConical, to: "/services/card-rehabilitation", copy: "Multiple treatments combined into one custom plan." },
  { name: "Pre-Grading Preparation", icon: ClipboardCheck, to: "/services/pre-grading-card-preparation", copy: "Evaluate and prepare appropriate cards before grading." },
  { name: "Safe Slab Removal", icon: PackageOpen, to: "/services/safe-slab-removal", copy: "Careful crack-out from cracked, damaged or unwanted slabs." },
];

const CHIPS = [
  { name: "Surface Cleaning", tag: "REMOVE DIRT & RESIDUE", icon: Sparkles },
  { name: "Scratch Reduction", tag: "IMPROVE APPEARANCE", icon: ScanLine },
  { name: "Crease Minimization", tag: "CONTROLLED IMPROVEMENT", icon: Layers },
  { name: "Safe Slab Removal", tag: "CARD-FIRST HANDLING", icon: PackageOpen },
];

const TRUST = [
  { label: "COLLECTOR FOCUSED", icon: ShieldCheck },
  { label: "PRESERVATION MINDED", icon: FlaskConical },
  { label: "CARD-FIRST HANDLING", icon: Diamond },
];

export const CardsShowcase = () => {
  return (
    <section data-testid="cards-showcase" className="relative bg-ink">
      {/* Main collectible visual — blends into black bg */}
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        <div className="relative mx-auto w-full lg:w-[88%]">
          <img
            src={ASSETS.cardsHero}
            alt="CardScienceHQ trading card cleaning, preparation, crease reduction, scratch reduction and safe slab removal services for sports cards and trading cards"
            width={1672}
            height={941}
            loading="lazy"
            decoding="async"
            className="w-full h-auto select-none"
          />
          <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent, #060608)" }} />
          <div className="absolute inset-y-0 left-0 w-16 pointer-events-none" style={{ background: "linear-gradient(90deg, #060608, transparent)" }} />
          <div className="absolute inset-y-0 right-0 w-16 pointer-events-none" style={{ background: "linear-gradient(270deg, #060608, transparent)" }} />
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 py-16 sm:py-20">
        {/* Wide range */}
        <p className="eyebrow mb-3">A Wide Range Of Collectibles</p>
        <h2 className="h-display text-3xl sm:text-4xl text-chrome">A WIDE RANGE OF COLLECTIBLES.</h2>
        <p className="text-[color:var(--chrome-2)] leading-relaxed mt-4 max-w-3xl">
          CardScience HQ evaluates every collectible individually. Different card stocks, coatings, finishes, ages and
          types of damage require different approaches. Our goal is to safely improve presentation while preserving the
          integrity of the collectible.
        </p>

        {/* Category grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mt-8">
          {CATEGORIES.map((c) => (
            <Link key={c.name} to={c.to} data-testid={`cat-${c.name}`}
              className="group rounded-2xl border border-[color:var(--line)] bg-white/[0.02] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[color:var(--cyan)]/50 hover:bg-white/[0.04]">
              <div className="h-9 w-9 rounded-lg border border-[color:var(--line-2)] flex items-center justify-center text-[color:var(--cyan)] group-hover:border-[color:var(--cyan)]/60 transition-colors">
                <Diamond size={16} />
              </div>
              <h3 className="font-display font-bold text-white text-base mt-4">{c.name}</h3>
              <p className="text-xs text-[color:var(--chrome-2)] leading-relaxed mt-1.5">{c.copy}</p>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-tech tracking-widest text-[color:var(--cyan)] mt-3 group-hover:gap-2.5 transition-all">
                LEARN MORE <ArrowRight size={12} />
              </span>
            </Link>
          ))}
        </div>

        {/* How we can help */}
        <p className="eyebrow mb-3 mt-16">How We Can Help</p>
        <h2 className="h-display text-3xl sm:text-4xl text-chrome">SERVICES BUILT AROUND THE CARD.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-8">
          {SERVICES.map((s) => (
            <Link key={s.name} to={s.to} data-testid={`svc-${s.name}`}
              className="group rounded-2xl border border-[color:var(--line)] bg-white/[0.02] p-5 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-[color:var(--violet)]/50 hover:bg-white/[0.04]">
              <div className="h-9 w-9 rounded-lg border border-[color:var(--line-2)] flex items-center justify-center text-[color:var(--cyan)]">
                <s.icon size={16} />
              </div>
              <h3 className="font-display font-bold text-white text-base mt-4">{s.name}</h3>
              <p className="text-xs text-[color:var(--chrome-2)] leading-relaxed mt-1.5 flex-1">{s.copy}</p>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-tech tracking-widest text-[color:var(--cyan)] mt-3 group-hover:gap-2.5 transition-all">
                EXPLORE <ArrowRight size={12} />
              </span>
            </Link>
          ))}
        </div>

        {/* Feature chips */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
          {CHIPS.map((c) => (
            <div key={c.name} className="rounded-xl border border-[color:var(--line)] bg-white/[0.03] px-4 py-3.5 flex items-center gap-3 transition-shadow hover:shadow-[0_0_24px_rgba(36,224,255,0.10)]">
              <c.icon size={18} className="text-[color:var(--cyan)] shrink-0" />
              <div>
                <p className="text-sm font-semibold text-white leading-tight">{c.name}</p>
                <p className="text-[10px] font-tech tracking-widest text-[color:var(--chrome-3)] mt-0.5">{c.tag}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Trust strip */}
        <div className="rounded-3xl holo-border mt-10 p-8 sm:p-10 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
          <div>
            <h2 className="h-display text-2xl sm:text-3xl text-chrome">EVERY COLLECTIBLE IS EVALUATED INDIVIDUALLY.</h2>
            <p className="text-[color:var(--chrome-2)] leading-relaxed mt-3 max-w-xl">
              Services and techniques vary by collectible. A process appropriate for one card may not be appropriate for another.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {TRUST.map((t) => (
              <div key={t.label} className="flex flex-col items-center text-center gap-2">
                <t.icon size={22} className="text-[color:var(--cyan)]" />
                <span className="text-[10px] font-tech tracking-widest text-[color:var(--chrome-2)] leading-tight">{t.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
          <Link to="/submit" data-testid="cards-cta-primary" className="btn-holo w-full sm:w-auto rounded-xl px-9 py-4 text-sm inline-flex items-center justify-center gap-2">
            START MY CARD EVALUATION <ArrowRight size={16} />
          </Link>
          <Link to="/services" data-testid="cards-cta-secondary" className="btn-ghost w-full sm:w-auto rounded-xl px-9 py-4 text-sm text-center">
            VIEW CARD SERVICES
          </Link>
        </div>
      </div>
    </section>
  );
};
